/**
 * Workspace FS helpers — thin wrappers around `workspace/*` Engine RPC methods.
 *
 * These helpers provide a convenient imperative API for the file-browser dialogs
 * (FileBrowser, SaveDialog, FilePickerDialog, FolderPickerDialog, BrowserSidebar).
 *
 * All operations route through `getPlatformService().engine.workspace*()` methods,
 * which in turn call Engine → fs sidecar.
 *
 * Types re-exported from `@magic-md/engine-client/protocol` for convenience.
 */

import type {
    WorkspaceAllowedRoot,
    WorkspaceGetInfoResult,
    WsFsEntry,
    WsFsStat,
} from "@magic-md/engine-client/protocol";
import { getPlatformService } from "../platform";

/* ── Re-exported Types (backward compat aliases) ── */

/** @deprecated Use `WsFsEntry` from `@magic-md/engine-client/protocol` */
export type FsEntry = WsFsEntry;

/** @deprecated Use `WsFsStat` from `@magic-md/engine-client/protocol` */
export type FsStat = WsFsStat;

export interface FsPathInfo {
    sep: string;
    os: string;
}

/* ── Helper ── */

function engine() {
    return getPlatformService().engine;
}

async function loadInfo(): Promise<WorkspaceGetInfoResult> {
    return engine().workspaceGetInfo();
}

/* ── API ── */

/** Workspace file operations via Engine `workspace/*` RPC methods. */
export const workspaceFs = {
    async info(): Promise<WorkspaceGetInfoResult> {
        return loadInfo();
    },

    /** List directory contents */
    async readDir(dirPath: string): Promise<WsFsEntry[]> {
        const { entries } = await engine().workspaceReadDir({ path: dirPath });
        return entries;
    },

    /** List directory contents, returning an empty list when the directory is absent. */
    async readOptionalDir(dirPath: string): Promise<WsFsEntry[]> {
        const { entries } = await engine().workspaceReadDir({
            path: dirPath,
            allowMissing: true,
        });
        return entries;
    },

    /** Get file/directory metadata */
    async stat(filePath: string): Promise<WsFsStat> {
        return engine().workspaceStat({ path: filePath });
    },

    /** Get the user's home directory */
    async home(): Promise<string> {
        const info = await loadInfo();
        return info.home;
    },

    async allowedRoots(): Promise<WorkspaceAllowedRoot[]> {
        const info = await loadInfo();
        return info.allowedRoots ?? [];
    },

    /** Get path separator and OS info */
    async pathInfo(): Promise<FsPathInfo> {
        const info = await loadInfo();
        return { sep: info.sep, os: info.os };
    },

    /**
     * Get a reasonable default directory for file dialogs.
     * Uses the first workspace folder if available, then home, then "/".
     */
    async defaultDir(): Promise<string> {
        try {
            const info = await loadInfo();
            if (info.folders.length > 0) return info.folders[0].path;
            if (info.allowedRoots.length > 0) return info.allowedRoots[0].path;
            return "";
        } catch {
            return "";
        }
    },
};
